import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {useFavourites} from '../../common/contexts/FavouritesContext';
import {useAuth} from '../../common/contexts/AuthContext';
import {Point} from '../../admin/artisticobjects/types/Image';
import {getObjectPosition} from '../../common/utils/imageUtils';
import './GalleryItem.scss';
import ShareButton from '../../common/components/ShareButton';

interface GalleryItemProps {
  title: string;
  location: string;
  image: {
    src: string;
    alt: string;
    focalPoint?: Point;
    size?: { width: number; height: number };
  };
  caption?: string;
  dating?: string;
  zoomedIn: boolean;
  withDescriptions: boolean;
  artisticObjectId: string;
  placeId: string;
}

const GalleryItem: React.FC<GalleryItemProps> = ({
  title,
  location,
  image,
  caption,
  dating,
  zoomedIn,
  withDescriptions,
  artisticObjectId,
  placeId
}) => {
  const {t} = useTranslation();
  const { isAuthenticated } = useAuth();
  const { isFavourited, toggleFavourite } = useFavourites();
  const [isTogglingFavourite, setIsTogglingFavourite] = useState(false);

  const artisticObjectUrl = `/gallery/artistic-object/${artisticObjectId}`;
  const placeUrl = `/gallery/place/${placeId}`;

  const handleFavouriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    setIsTogglingFavourite(true);
    try {
      await toggleFavourite(artisticObjectId, isAuthenticated);
    } finally {
      setIsTogglingFavourite(false);
    }
  };

  const imageStyle = getObjectPosition(image.focalPoint, image.size);

  return (
    <div className="gallery-item bg-white rounded shadow-sm overflow-hidden">
      <Link to={artisticObjectUrl} className="block h-64 overflow-hidden relative flex justify-center bg-black">
        <img
          className={`zoomedIn ${zoomedIn ? 'w-full' : ''} h-full object-cover`}
          src={image.src}
          alt={image.alt}
          style={imageStyle}
        />
        {caption && (
          <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-sm">
            {caption}
          </div>
        )}
        {!withDescriptions && (
          <div className="absolute bottom-2 left-2">
            <button
              className={`bg-black bg-opacity-70 hover:bg-opacity-90 p-2 rounded-sm ${isFavourited(artisticObjectId) ? 'text-red-500 hover:text-red-700' : 'text-white hover:text-gray-300'} ${isTogglingFavourite ? 'opacity-50 cursor-wait' : ''}`}
              onClick={handleFavouriteClick}
              disabled={isTogglingFavourite}
              title={isFavourited(artisticObjectId) ? t('gallery.item.removeFromFavourites') : t('gallery.item.addToFavourites')}
              data-testid="toggle-favourite-image"
            >
              <i className={`${isFavourited(artisticObjectId) ? 'fa fa-heart' : 'fa-regular fa-heart'}`}/>
            </button>
          </div>
        )}
      </Link>
      <div className={`p-4 ${withDescriptions ? '' : 'hidden'}`}>
        <h3 className="gallery-item-title font-medium text-gray-900">
          <Link to={artisticObjectUrl} className="hover:text-gray-800 hover:underline">
            {title}
          </Link>
        </h3>
        <div className="flex justify-between items-top gap-2 mt-2 text-sm text-gray-600">
          <Link to={placeUrl} className="hover:text-gray-800 hover:underline">
            {location}
          </Link>
          {dating && <span>{dating}</span>}
        </div>
        <div className="flex items-center mt-3">
          <button
            className={`${isFavourited(artisticObjectId) ? 'text-red-500 hover:text-red-700' : 'text-gray-500 hover:text-gray-700'} ${isTogglingFavourite ? 'opacity-50 cursor-wait' : ''}`}
            onClick={handleFavouriteClick}
            disabled={isTogglingFavourite}
            title={isFavourited(artisticObjectId) ? t('gallery.item.removeFromFavourites') : t('gallery.item.addToFavourites')}
            data-testid="toggle-favourite"
          >
            <i className={`${isFavourited(artisticObjectId) ? 'fa fa-heart' : 'fa-regular fa-heart'}`}/>
          </button>
          <ShareButton
            title={title}
            text={`${title} - ${location}`}
            url={`${window.location.origin}/gallery/artistic-object/${artisticObjectId}`}
            className="text-gray-500 hover:text-gray-700 ml-3"
          />
          <Link
            to={artisticObjectUrl}
            className="ml-auto bg-gray-800 hover:bg-black text-white text-xs px-3 py-1 rounded-sm"
          >
            {t('gallery.item.viewDetails')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default GalleryItem;
